import type { CartKey, CartProductLimit } from '~/stores/cart.store'

import { useCart } from '~/composables/useCart'

interface ProductWithCartLimit {
    slug?: CartKey | null
    max_cart_quantity?: number | string | null
}

type ProductWithRequiredCartLimit = ProductWithCartLimit & {
    slug: CartKey
    max_cart_quantity: NonNullable<ProductWithCartLimit['max_cart_quantity']>
}

export const mapProductsToCartLimits = (
    products?: ProductWithCartLimit[] | null
): CartProductLimit[] => {
    if (!products?.length) {
        return []
    }

    return products
        .filter(
            (product): product is ProductWithRequiredCartLimit =>
                product.slug != null && product.max_cart_quantity != null
        )
        .map((product) => ({
            slug: product.slug,
            max_cart_quantity: product.max_cart_quantity,
        }))
}

export const useCartProductLimits = () => {
    const { enforceProductLimits } = useCart()

    const applyProductLimits = (products?: ProductWithCartLimit[] | null) => {
        const limits = mapProductsToCartLimits(products)

        if (!limits.length) {
            return
        }

        enforceProductLimits(limits)
    }

    const applyProductLimit = (product?: ProductWithCartLimit | null) => {
        applyProductLimits(product ? [product] : [])
    }

    return {
        applyProductLimit,
        applyProductLimits,
    }
}
